import React, { useState, useEffect } from "react";
import { Modal, Select, InputNumber, Button, Divider, Input, message, Table, Form } from "antd";
import { PrinterOutlined } from "@ant-design/icons";
import { GetBarcodeTemplates } from "../../../Api";
import { generateCode128SvgPath } from "../../../Utils";
import { modalTitle, modalFooter } from "../../../components";

interface BulkPrintItem {
  id?: string;
  title: string;
  sku_code: string;
  barcode: string;
  mrp?: number;
  retail_price?: number;
  cost_price?: number;
  quantity: number;
}

interface BulkBarcodePrintModalProps {
  open: boolean;
  onClose: () => void;
  items: BulkPrintItem[];
}

export const BulkBarcodePrintModal: React.FC<BulkBarcodePrintModalProps> = ({
  open,
  onClose,
  items
}) => {
  const [templates, setTemplates] = useState<any[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [printCols, setPrintCols] = useState<number>(4);
  const [printMarginTop, setPrintMarginTop] = useState<number>(15);
  const [printMarginLeft, setPrintMarginLeft] = useState<number>(10);
  const [printColGap, setPrintColGap] = useState<number>(5);
  const [printRowGap, setPrintRowGap] = useState<number>(5);
  const [costCodeAlphabet, setCostCodeAlphabet] = useState<string>("COMPUTERSX");
  const [expiryDaysOffset, setExpiryDaysOffset] = useState<number>(365);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      loadTemplates();
    }
  }, [open]);

  const loadTemplates = async () => {
    setLoading(true);
    const res = await GetBarcodeTemplates();
    if (res.success && res.data?.length > 0) {
      const parsed = res.data.map((t: any) => ({
        ...t,
        elements: typeof t.elements === "string" ? JSON.parse(t.elements) : t.elements,
      }));
      setTemplates(parsed);
      
      const defaultTpl = parsed.find((t: any) => t.is_default) || parsed[0];
      if (defaultTpl) {
        setSelectedTemplateId(defaultTpl.id);
      }
    } else {
      message.warning("No barcode templates found. Please create one in Barcode Label Editor first.");
    }
    setLoading(false);
  };

  const generateCostCode = (price: number, alphabet: string): string => {
    const cipher = alphabet.toUpperCase().padEnd(10, "X");
    const digits = price.toFixed(0).toString();
    return digits
      .split("")
      .map((d) => {
        const idx = parseInt(d, 10);
        return isNaN(idx) ? d : cipher[idx];
      })
      .join("");
  };

  const getElementPreviewText = (text: string, item: BulkPrintItem) => {
    if (!text) return "";
    let formatted = text;

    formatted = formatted.replace(/{product_name}/gi, item.title || "Product Name");
    formatted = formatted.replace(/{sku}/gi, item.sku_code || "SKU-0000");
    formatted = formatted.replace(/{barcode}/gi, item.barcode || "12345678");

    const price = parseFloat((item.mrp as any) || (item as any).retail_price || 0);
    formatted = formatted.replace(/{price}/gi, price.toLocaleString(undefined, { minimumFractionDigits: 2 }));

    const cost = Number(item?.cost_price || 0);
    formatted = formatted.replace(/{cost_price}/gi, cost.toLocaleString(undefined, { minimumFractionDigits: 2 }));
    formatted = formatted.replace(/{cost_code}/gi, generateCostCode(cost, costCodeAlphabet));

    const expDate = new Date();
    expDate.setDate(expDate.getDate() + expiryDaysOffset);
    const expStr = expDate.toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit" });
    formatted = formatted.replace(/{expiry_date}/gi, expStr);

    return formatted;
  };

  const handlePrint = () => {
    const template = templates.find(t => t.id === selectedTemplateId);
    if (!template) {
      message.error("Please select a template first.");
      return;
    }

    const scale = 3.78; // 1mm = 3.78px
    const canvasWidthPx = template.width * scale;
    const canvasHeightPx = template.height * scale;

    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const labelsHtml = items
      .map((item) => {
        const itemHtml = Array.from({ length: item.quantity || 1 })
          .map(() => {
            return `
              <div class="label-item">
                ${template.elements.map((el: any) => {
                  const previewText = getElementPreviewText(el.text || "", item);
                  
                  if (el.type === "text") {
                    return `
                      <div style="
                        position: absolute;
                        left: ${el.x}px;
                        top: ${el.y}px;
                        width: ${el.w}px;
                        height: ${el.h}px;
                        font-size: ${el.fontSize}px;
                        font-weight: ${el.fontWeight};
                        text-align: ${el.align};
                        transform: rotate(${el.rotation || 0}deg);
                        white-space: nowrap;
                        overflow: hidden;
                        font-family: 'Inter', sans-serif;
                      ">
                        ${previewText}
                      </div>
                    `;
                  } else if (el.type === "barcode") {
                    const svgInfo = generateCode128SvgPath(previewText || "12345678");
                    return `
                      <div style="
                        position: absolute;
                        left: ${el.x}px;
                        top: ${el.y}px;
                        width: ${el.w}px;
                        height: ${el.h}px;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                      ">
                        <svg viewBox="0 0 ${svgInfo.width} 100" style="width: 100%; height: 100%;">
                          <path d="${svgInfo.path}" stroke="black" stroke-width="2" fill="none" />
                        </svg>
                      </div>
                    `;
                  } else if (el.type === "qrcode") {
                    const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(previewText || "12345678")}`;
                    return `
                      <img src="${qrUrl}" style="
                        position: absolute;
                        left: ${el.x}px;
                        top: ${el.y}px;
                        width: ${el.w}px;
                        height: ${el.h}px;
                        object-fit: contain;
                      " />
                    `;
                  } else if (el.type === "logo") {
                    return `<div style="position: absolute; left: ${el.x}px; top: ${el.y}px; width: ${el.w}px; height: ${el.h}px; border: 1px dashed #ccc;"></div>`;
                  } else if (el.type === "line") {
                    return `
                      <div style="
                        position: absolute;
                        left: ${el.x}px;
                        top: ${el.y}px;
                        width: ${el.w}px;
                        height: ${el.h}px;
                        background-color: black;
                      "></div>
                    `;
                  }
                  return "";
                }).join("")}
              </div>
            `;
          })
          .join("");
        return itemHtml;
      })
      .join("");

    printWindow.document.write(`
      <html>
        <head>
          <title>Bulk Print Barcodes</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
            body {
              margin: 0;
              padding: 0;
              background-color: white;
            }
            .labels-container {
              display: grid;
              grid-template-columns: repeat(${printCols}, ${canvasWidthPx}px);
              column-gap: ${printColGap}px;
              row-gap: ${printRowGap}px;
              margin-left: ${printMarginLeft}px;
              margin-top: ${printMarginTop}px;
            }
            .label-item {
              position: relative;
              width: ${canvasWidthPx}px;
              height: ${canvasHeightPx}px;
              border: 1px solid #ddd;
              box-sizing: border-box;
              overflow: hidden;
              page-break-inside: avoid;
            }
            @media print {
              .label-item {
                border: none;
              }
              button, .no-print {
                display: none;
              }
            }
          </style>
        </head>
        <body>
          <div class="labels-container">
            ${labelsHtml}
          </div>
          <script>
            window.onload = function() {
              setTimeout(() => { window.print(); }, 500);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
    onClose();
  };

  return (
    <Modal
      title={modalTitle("Bulk Print Barcode Labels")}
      open={open}
      onCancel={onClose}
      footer={modalFooter({
        actions: [
          {
            label: "Cancel",
            onClick: onClose,
          },
          {
            label: (
              <span>
                <PrinterOutlined style={{ marginRight: 6 }} />
                Print Bulk Labels
              </span>
            ),
            type: "primary",
            onClick: handlePrint,
            disabled: !selectedTemplateId || items.length === 0,
          },
        ],
      })}
      width={600}
    >
      <Form layout="vertical" className="py-3">
        <Form.Item label="Select Label Template">
          <Select
            className="w-full"
            placeholder="Choose Template"
            value={selectedTemplateId}
            onChange={setSelectedTemplateId}
            loading={loading}
          >
            {templates.map((t) => (
              <Select.Option key={t.id} value={t.id}>
                {t.name} {t.is_default && "(Default)"}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Columns (per row)">
          <InputNumber
            min={1}
            max={12}
            value={printCols}
            onChange={(val) => setPrintCols(val || 4)}
            className="w-full"
          />
        </Form.Item>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item label="Cost Code Cipher Key">
            <Input
              value={costCodeAlphabet}
              onChange={(e) => setCostCodeAlphabet(e.target.value)}
              maxLength={10}
              placeholder="COMPUTERSX"
              className="uppercase font-mono"
            />
          </Form.Item>
          <Form.Item label="Expiry Date Offset (Days)">
            <InputNumber
              min={0}
              value={expiryDaysOffset}
              onChange={(val) => setExpiryDaysOffset(val || 0)}
              className="w-full"
            />
          </Form.Item>
        </div>

        <Divider className="my-1">Sheet Margins (px)</Divider>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item label="Top Margin">
            <InputNumber
              min={0}
              value={printMarginTop}
              onChange={(val) => setPrintMarginTop(val || 0)}
              className="w-full"
            />
          </Form.Item>
          <Form.Item label="Left Margin">
            <InputNumber
              min={0}
              value={printMarginLeft}
              onChange={(val) => setPrintMarginLeft(val || 0)}
              className="w-full"
            />
          </Form.Item>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item label="Col Gap">
            <InputNumber
              min={0}
              value={printColGap}
              onChange={(val) => setPrintColGap(val || 0)}
              className="w-full"
            />
          </Form.Item>
          <Form.Item label="Row Gap">
            <InputNumber
              min={0}
              value={printRowGap}
              onChange={(val) => setPrintRowGap(val || 0)}
              className="w-full"
            />
          </Form.Item>
        </div>

        <Divider className="my-1">Items Included ({items.length})</Divider>

        <div className="max-h-40 overflow-y-auto border rounded border-gray-200">
          <Table
            size="small"
            dataSource={items}
            rowKey="barcode"
            pagination={false}
            columns={[
              { title: "Item", dataIndex: "title" },
              { title: "Barcode", dataIndex: "barcode" },
              { title: "Print Qty", dataIndex: "quantity", align: "right" },
            ]}
          />
        </div>
      </Form>
    </Modal>
  );
};
